import {
  clientWithFork,
  customerControllerEditCustomer,
  getNewBranchIdAfterFork,
  policyControllerEditPolicy,
} from 'korint-sdk';
import type { AfterSubmitResult } from 'korint-sdk/react';

export type AfterSubmitFn = (
  data: Record<string, unknown>,
  params: { customerId: string; policyId: string; branchId: string; isDirty: boolean },
) => AfterSubmitResult | Promise<AfterSubmitResult>;

export type CleanupPredicate = (data: Record<string, unknown>) => boolean;

export const isNaturalPerson: CleanupPredicate = data =>
  data.legalNaturalPerson === 'NATURAL_PERSON' || data.legalNaturalPerson === true;

export const isCompanyBeingCreated: CleanupPredicate = data => data.isCompanyBeingCreated === true;

export const isNaturalPersonOrCompanyBeingCreated: CleanupPredicate = data =>
  isNaturalPerson(data) || isCompanyBeingCreated(data);

export function composeAfterSubmitFns(...fns: AfterSubmitFn[]): AfterSubmitFn {
  return async (data, params) => {
    let branchId = params.branchId;
    for (const fn of fns) {
      const result = await fn(data, { ...params, branchId });
      branchId = result?.branchId ?? branchId;
    }
    return { branchId };
  };
}

export function buildFieldCleanup(shouldCleanup: CleanupPredicate, keys: string[]): AfterSubmitFn {
  return async (data, params) => {
    if (!params.isDirty || keys.length === 0 || !shouldCleanup(data)) return;

    const response = await customerControllerEditCustomer(
      params.customerId,
      {
        customFields: keys.map(key => ({ key, value: null })),
      },
      { branchId: params.branchId },
      { client: clientWithFork },
    );
    return { branchId: getNewBranchIdAfterFork(response) };
  };
}

/**
 * Twin of `buildFieldCleanup` but targets policy customFields instead of customer ones.
 * Use this in a `policyFunnel` step's `afterSubmit` to nullify orphan policy custom fields.
 **/
export function buildPolicyFieldCleanup(
  shouldCleanup: CleanupPredicate,
  keys: string[],
): AfterSubmitFn {
  return async (data, params) => {
    if (keys.length === 0 || !shouldCleanup(data)) return;

    const response = await policyControllerEditPolicy(
      params.policyId,
      {
        customFields: keys.map(key => ({ key, value: null })),
      },
      { branchId: params.branchId },
      { client: clientWithFork },
    );
    return { branchId: getNewBranchIdAfterFork(response) };
  };
}
